import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    MESSAGE_TYPES,
    RESIZE_THROTTLE_MS,
    createEmbedEnvelope,
    getAllowedOriginFromSrc,
    isChildToParentMessage,
    normalizeResizeHeight,
    throttleResize,
} from '../protocol/embedProtocol';

export function useIframeEmbedBridge({
    allowedOrigin: allowedOriginProp,
    getAuthContext,
    iframeRef,
    iframeSrc,
    initContext,
    initExtras,
    isActive,
    maxIframeHeight,
    mode,
    onCloseFromEmbed,
    onEmbedReady,
    onInitError,
    onNotify,
    onStateChange,
}) {
    const allowedOrigin = useMemo(() => {
        if (allowedOriginProp) return allowedOriginProp;
        return getAllowedOriginFromSrc(iframeSrc) ?? '';
    }, [allowedOriginProp, iframeSrc]);

    const [iframePixelHeight, setIframePixelHeight] = useState(null);
    const [iframeState, setIframeState] = useState(null);
    const initSentRef = useRef(false);

    const sendInitToIframe = useCallback(
        async (request = {}) => {
            const contentWindow = iframeRef.current?.contentWindow;
            if (!contentWindow || !allowedOrigin) return;

            let resolvedAuthContext = {};

            if (getAuthContext) {
                resolvedAuthContext = await Promise.resolve(
                    getAuthContext({
                        iframeSrc,
                        initContext: initContext ?? {},
                        mode,
                        request,
                    }),
                );
            }

            const payload = {
                ...(mode !== undefined ? { mode } : {}),
                ...(initExtras ?? {}),
                ...(initContext ?? {}),
                ...(resolvedAuthContext ?? {}),
            };

            contentWindow.postMessage(
                createEmbedEnvelope(MESSAGE_TYPES.INIT, payload),
                allowedOrigin,
            );
            initSentRef.current = true;
            onEmbedReady?.();
        },
        [
            allowedOrigin,
            getAuthContext,
            iframeRef,
            iframeSrc,
            initContext,
            initExtras,
            mode,
            onEmbedReady,
        ],
    );

    const updateIframeHeight = useMemo(
        () =>
            throttleResize((height) => {
                let nextHeight = height;
                if (Number.isFinite(maxIframeHeight)) {
                    nextHeight = Math.min(nextHeight, maxIframeHeight);
                }
                setIframePixelHeight(nextHeight);
            }, RESIZE_THROTTLE_MS),
        [maxIframeHeight],
    );

    const sendActionToIframe = useCallback(
        (action, payload = {}) => {
            const contentWindow = iframeRef.current?.contentWindow;
            if (!contentWindow || !allowedOrigin || !action) return false;

            contentWindow.postMessage(
                createEmbedEnvelope(MESSAGE_TYPES.ACTION, {
                    action,
                    ...(payload ?? {}),
                }),
                allowedOrigin,
            );

            return true;
        },
        [allowedOrigin, iframeRef],
    );

    useEffect(() => {
        if (!isActive) return undefined;

        initSentRef.current = false;
        setIframePixelHeight(null);
        setIframeState(null);
        onStateChange?.(null);

        const handleMessage = (event) => {
            if (!allowedOrigin || event.origin !== allowedOrigin) return;
            if (iframeRef.current && event.source !== iframeRef.current.contentWindow) return;
            if (!isChildToParentMessage(event.data)) return;

            switch (event.data.type) {
                case MESSAGE_TYPES.READY:
                    if (!initSentRef.current) {
                        sendInitToIframe({ reason: 'ready' }).catch((error) => {
                            onInitError?.(error);
                        });
                    }
                    break;
                case MESSAGE_TYPES.REQUEST_CONTEXT_REFRESH:
                    sendInitToIframe(event.data.payload ?? { reason: 'contextRefresh' }).catch(
                        (error) => {
                            onInitError?.(error);
                        },
                    );
                    break;
                case MESSAGE_TYPES.RESIZE: {
                    const payload = event.data.payload;
                    const rawHeight =
                        payload && typeof payload === 'object' && 'height' in payload
                            ? payload.height
                            : null;
                    const nextHeight = normalizeResizeHeight(rawHeight);
                    if (nextHeight !== null) updateIframeHeight(nextHeight);
                    break;
                }
                case MESSAGE_TYPES.STATE: {
                    const nextState = event.data.payload ?? null;
                    setIframeState(nextState);
                    onStateChange?.(nextState);
                    break;
                }
                case MESSAGE_TYPES.NOTIFY:
                    onNotify?.(event.data.payload ?? null);
                    break;
                case MESSAGE_TYPES.CLOSE:
                    onCloseFromEmbed();
                    break;
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [
        allowedOrigin,
        iframeRef,
        isActive,
        onCloseFromEmbed,
        onInitError,
        onNotify,
        onStateChange,
        sendInitToIframe,
        updateIframeHeight,
    ]);

    const resetBridge = useCallback(() => {
        initSentRef.current = false;
        setIframePixelHeight(null);
        setIframeState(null);
        onStateChange?.(null);
    }, [onStateChange]);

    return {
        allowedOrigin,
        iframePixelHeight,
        iframeState,
        resetBridge,
        sendActionToIframe,
    };
}
